import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React from 'react';
import { UploadControl } from '@/components/UploadControl';
import { VideoFeatures } from '@/store/videoFeatures';
import { useVideoFeaturesStore } from '@/store/videoFeatures/store';
import { ThumbnailType } from './types';
import { AlertTriangle } from 'lucide-react';

interface InitialThumbnailTabProps {
  features: VideoFeatures;
  handleInitialThumbnailUpload: (file: File) => void;
  removeThumbnail: (type: ThumbnailType) => void;
}

export const InitialThumbnailTab: React.FC<InitialThumbnailTabProps> = ({
  features,
  handleInitialThumbnailUpload,
  removeThumbnail,
}) => {
  useLanguage();
  const setTempFile = useVideoFeaturesStore((state) => state.setTempFile);
  const clearTempFile = useVideoFeaturesStore((state) => state.clearTempFile);

  const handleFileUpload = (file: File) => {
    setTempFile('initialThumbnailFile', file);
    handleInitialThumbnailUpload(file);
  };

  const handleRemove = (type: ThumbnailType) => {
    clearTempFile('initialThumbnailFile');
    removeThumbnail(type);
  };

  return (
    <div className="space-y-6">
      <div className="p-6 bg-white rounded-[14px] border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
        <UploadControl
          label=""
          accept="image/*"
          maxSize={20}
          preview={features.initialThumbnail?.imageUrl}
          onFileSelected={handleFileUpload}
          onClear={() => handleRemove('initialThumbnail')}
          isActive={features.initialThumbnail?.enabled}
        />
      </div>
      
      {/* Lógica de dependência: Controles ativados E Autoplay desativado */}
      {!features.showControls && features.initialThumbnail?.enabled && (
        <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-yellow-900">
              {translateInterfaceText("A thumbnail inicial só funciona se os Controles de Vídeo estiverem ativados. Ative os Controles nas configurações.")}</p>
          </div>
        </div>
      )}
      
      {features.autoPlay && features.initialThumbnail?.enabled && (
        <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-yellow-900">
              {translateInterfaceText("Para exibir a thumbnail de início, desabilite a reprodução automática.")}</p>
          </div>
        </div>
      )}
    </div>
  );
};
